import React, { useEffect, useState, useCallback } from "react";
import { Check, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { formatINR } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";

export default function Plan() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [topups, setTopups] = useState([]);
  const [biz, setBiz] = useState(null);
  const [sub, setSub] = useState(null);
  const [billing, setBilling] = useState("monthly");
  const [buyingTopup, setBuyingTopup] = useState(null);

  // Solo agent = agent with no broker_admin_id
  const isSoloAgent = user?.role === "agent" && !user?.broker_admin_id;
  const isBrokerAgent = user?.role === "agent" && !!user?.broker_admin_id;

  const load = useCallback(async () => {
    const [p, b, t, s] = await Promise.all([
      api.get("/plans"),
      api.get("/business").catch(() => ({ data: null })),
      api.get("/slot-topups"),
      api.get("/subscription").catch(() => ({ data: null })),
    ]);

    let filteredPlans = p.data;

    // Solo agents only see Free + Starter (renamed Pro)
    if (isSoloAgent) {
      filteredPlans = p.data
        .filter(pl => ["free", "starter"].includes(pl.id))
        .map(pl => pl.id === "starter" ? { ...pl, name: "Pro" } : pl);
    }

    setPlans(filteredPlans);
    setBiz(b.data);
    setTopups(t.data);
    setSub(s.data);
  }, [isSoloAgent]);

  useEffect(() => { load(); }, [load]);

  const upgrade = async (id) => {
    try {
      const { data } = await api.post("/plans/upgrade", { plan: id, billing });
      if (data.intro_applied) {
        toast.success(`You're on ${id} — ${formatINR(data.amount_due_paise)} for your first month, then ${formatINR(data.regular_amount_paise)}.`);
      } else {
        toast.success(`Switched to ${id} — ${billing} billing.`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const buyTopup = async (topup) => {
    setBuyingTopup(topup.id);
    try {
      await api.post("/slot-topups/buy", { topup_id: topup.id });
      toast.success(`${topup.slots} slots added to your account!`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBuyingTopup(null);
    }
  };

  // Agents under a broker have no plan access
  if (isBrokerAgent) {
    return (
      <div className="space-y-6" data-testid="plan-page">
        <div>
          <h1 className="text-2xl font-bold font-display">Plan & Billing</h1>
          <p className="text-sm text-gray-500 mt-1">Your slots are managed by your broker.</p>
        </div>
        <div className="card-base p-8 text-center">
          <div className="text-4xl mb-3">🏢</div>
          <h2 className="font-semibold text-gray-800">You're on your broker's plan</h2>
          <p className="text-sm text-gray-500 mt-2">Your agreement slots come from your broker's subscription. Contact your broker to get more slots.</p>
        </div>

        {/* Top-up still available for broker agents */}
        <div className="card-base p-6">
          <div className="flex items-center gap-2 mb-1">
            <Zap className="h-4 w-4 text-orange-500" />
            <h2 className="font-semibold text-sm">Need a few more slots?</h2>
          </div>
          <p className="text-xs text-gray-500 mb-4">Top up your account without changing your plan. Slots never expire.</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {topups.map(t => (
              <div key={t.id} className="border border-gray-200 rounded-xl p-4 text-center hover:border-indigo-300 transition-colors">
                <div className="text-lg font-bold font-display text-[#F97316]">{t.slots}</div>
                <div className="text-xs text-gray-500 mb-2">slots</div>
                <div className="text-sm font-semibold">{formatINR(t.price)}</div>
                <button
                  onClick={() => buyTopup(t)}
                  disabled={buyingTopup === t.id}
                  className="mt-2 w-full py-1.5 text-xs font-medium rounded-lg bg-orange-500 hover:bg-orange-600 text-white btn-press disabled:opacity-50"
                >
                  {buyingTopup === t.id ? "Adding…" : "Buy"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const current = biz?.subscription_plan || "free";
  // The intro offer is account-wide (any plan), so take it off the first paid
  // plan that reports eligibility rather than repeating it per card.
  const introOffer = plans.find(p => p.intro_eligible) || null;

  return (
    <div className="space-y-8" data-testid="plan-page">
      <div>
        <h1 className="text-2xl font-bold font-display">Plan & Billing</h1>
        <p className="text-sm text-gray-500 mt-1">You're on <span className="font-medium capitalize">{current}</span>.</p>
      </div>

      {/* Current subscription state */}
      {sub && sub.plan !== "free" && (
        <div className="card-base p-5 space-y-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-semibold text-gray-900 capitalize">
              {sub.plan} · {sub.billing}
              {sub.in_intro_period && (
                <span className="ml-2 pill bg-[#FFF4ED] text-[#F97316] text-[10px]">Intro month</span>
              )}
              {sub.status === "past_due" && (
                <span className="ml-2 pill bg-amber-100 text-amber-800 text-[10px]">Renewal due</span>
              )}
            </div>
            {sub.current_period_end && (
              <div className="text-xs text-gray-500">
                Current period ends {new Date(sub.current_period_end).toLocaleDateString("en-IN")}
              </div>
            )}
          </div>
          {sub.in_intro_period && (
            <p className="text-sm text-gray-600">
              You're paying {formatINR(sub.current_period_amount_paise)} for this month. From the next period it's{" "}
              {formatINR(sub.next_amount_paise)}.
            </p>
          )}

          {/* Failed-payment recovery */}
          {sub.status === "suspended" && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
              <div className="font-semibold">Payment failed — agreement creation is paused</div>
              <p className="text-xs mt-1">
                We'll retry every {sub.retry_interval_days} days
                {sub.next_retry_at && <> (next on {new Date(sub.next_retry_at).toLocaleDateString("en-IN")})</>}.
                Attempt {sub.dunning_attempts} of {sub.dunning_max_attempts} — after that the subscription is
                cancelled. Update your payment method to restore access straight away.
              </p>
            </div>
          )}
          {sub.status === "cancelled" && (
            <div className="rounded-lg bg-gray-100 border border-gray-300 px-3 py-2 text-sm text-gray-700">
              <div className="font-semibold">Subscription cancelled</div>
              <p className="text-xs mt-1">Pick a plan below to start again. Agreement creation stays paused until you do.</p>
            </div>
          )}
          {/* Honest about the current state: nothing is being auto-charged yet. */}
          {!sub.auto_debit_enabled && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Automatic renewal isn't switched on yet — nothing is charged to you automatically. We'll ask you to
              set up auto-debit before your first real renewal.
            </p>
          )}
        </div>
      )}

      {/* Billing toggle */}
      <div className="flex items-center gap-3">
        <button onClick={() => setBilling("monthly")} className={`px-4 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${billing === "monthly" ? "border-indigo-500 bg-[#FFF4ED] text-indigo-900" : "border-gray-200 text-gray-600"}`}>
          Monthly
        </button>
        <button onClick={() => setBilling("yearly")} className={`px-4 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${billing === "yearly" ? "border-indigo-500 bg-[#FFF4ED] text-indigo-900" : "border-gray-200 text-gray-600"}`}>
          Yearly <span className="ml-1 text-xs text-emerald-600 font-semibold">Save 33%</span>
        </button>
      </div>

      {/* Yearly benefit callout */}
      {billing === "yearly" && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-700 font-medium">
          🎉 Yearly plan — no monthly cap. Use your full quota any time during the year.
        </div>
      )}

      {/* Solo agent callout */}
      {isSoloAgent && (
        <div className="rounded-xl bg-[#FFF4ED] border border-indigo-200 px-4 py-3 text-sm text-indigo-900">
          💡 Want more features and agents? <span className="font-semibold">Upgrade to Broker</span> to unlock team management, analytics and more.
        </div>
      )}

      {/* Badge discount callout */}
      {plans[0]?.badge_discount_percent > 0 && (
        <div className="rounded-xl bg-cyan-50 border border-cyan-200 px-4 py-3 text-sm text-cyan-900">
          🏆 You're a valued top performer this year — <span className="font-semibold">{plans[0].badge_discount_percent}% off</span> every plan below is already applied. Keep closing deals to hold onto it next year.
        </div>
      )}

      {/* Launch offer — stated once, up front. Deliberately NOT repeated as the
          headline price on each card: showing ₹10 on all five made every plan
          look identically priced and buried the thing that actually
          differentiates them. */}
      {introOffer && (
        <div className="rounded-xl bg-gradient-to-r from-[#F97316] to-[#FB923C] px-5 py-4 text-white flex items-center gap-4 flex-wrap">
          <span className="text-2xl">🚀</span>
          <div>
            <div className="font-semibold">
              Launch offer — {formatINR(introOffer.intro_price_paise)} for your first{" "}
              {introOffer.intro_period_months === 1 ? "month" : `${introOffer.intro_period_months} months`}, on any plan.
            </div>
            <div className="text-sm text-white/90 mt-0.5">
              Pick the plan that fits — you're charged {formatINR(introOffer.intro_price_paise)} today, then the plan's
              regular price from month two. One-time offer, new subscribers only.
            </div>
          </div>
        </div>
      )}

      {/* Plan cards */}
      <div className={`grid grid-cols-1 gap-3 ${isSoloAgent ? "md:grid-cols-2 max-w-lg" : "md:grid-cols-5"}`}>
        {plans.map(p => {
          const isCur = current === p.id || (current === "starter" && p.id === "starter");
          const price = billing === "yearly" ? p.yearly_price : p.monthly_price;
          const discountedPrice = billing === "yearly" ? p.discounted_yearly_price : p.discounted_monthly_price;
          const hasDiscount = p.badge_discount_percent > 0 && price > 0;
          const showMonthlyCap = billing === "monthly" && p.monthly_cap && p.monthly_cap !== -1;
          return (
            <div key={p.id} className={`card-base p-5 relative ${p.popular ? "border-indigo-500 ring-2 ring-indigo-100" : ""} ${isCur ? "bg-[#FFF4ED]/40" : ""}`} data-testid={`plan-${p.id}`}>
              {/* Badges sit side by side rather than overlapping when a card is
                  both "Most popular" and the current plan. */}
              <div className="absolute -top-3 left-0 right-0 flex justify-center gap-1.5 px-2">
                {p.popular && <span className="pill bg-[#F97316] text-white text-[10px] whitespace-nowrap">Most popular</span>}
                {isCur && <span className="pill bg-emerald-500 text-white text-[10px] whitespace-nowrap">Current</span>}
              </div>

              <div className="text-sm font-semibold text-gray-700 mt-1">{p.name}</div>

              {/* Real, ongoing price as the headline — this is what actually
                  differentiates the plans. The ₹10 offer lives in the banner. */}
              <div className="mt-2">
                {hasDiscount && (
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-sm text-gray-400 line-through">{formatINR(price)}</span>
                    <span className="pill bg-cyan-100 text-cyan-700 text-[10px]">-{p.badge_discount_percent}%</span>
                  </div>
                )}
                <div className="text-3xl font-bold font-display text-gray-900 leading-none">
                  {price === 0 ? "₹0" : formatINR(hasDiscount ? discountedPrice : price)}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  per {billing === "yearly" ? "year" : "month"}
                </div>
                {billing === "yearly" && p.effective_monthly && (
                  <div className="text-sm text-emerald-600 font-medium mt-0.5">
                    ≈ ₹{(p.effective_monthly / 100).toLocaleString("en-IN")}/mo
                  </div>
                )}
                {p.intro_eligible && (
                  <div className="text-sm text-[#F97316] font-medium mt-1">
                    {formatINR(p.intro_price_paise)} first month
                  </div>
                )}
              </div>

              {/* "Pay for 8 months, get 12" describes the YEARLY discount — it's
                  meaningless (and misleading) while Monthly is selected. */}
              {billing === "yearly" && p.yearly_saving && (
                <p className="mt-3 text-sm text-emerald-700 font-medium">{p.tagline}</p>
              )}
              {p.id === "free" && <p className="mt-3 text-sm text-gray-600">{p.tagline}</p>}

              <ul className="mt-4 space-y-2 text-sm text-gray-700">
                <li className="flex gap-2">
                  <Check className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span className="font-medium">
                    {p.agreements_per_year === -1 ? "Unlimited agreements" : `${p.agreements_per_year} agreements/year`}
                  </span>
                </li>
                {showMonthlyCap && (
                  <li className="flex gap-2">
                    <Check className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <span className="text-amber-700">Up to {p.monthly_cap} per month</span>
                  </li>
                )}
                {!isSoloAgent && (
                  <li className="flex gap-2">
                    <Check className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>{p.agents === -1 ? "Unlimited agents" : `${p.agents} agent${p.agents > 1 ? "s" : ""}`}</span>
                  </li>
                )}
                {p.analytics && (
                  <li className="flex gap-2">
                    <Check className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>Analytics</span>
                  </li>
                )}
              </ul>
              <button
                data-testid={`upgrade-${p.id}`}
                disabled={isCur}
                onClick={() => upgrade(p.id)}
                className={`mt-5 w-full py-2.5 text-sm font-semibold rounded-lg btn-press ${isCur ? "bg-gray-100 text-gray-400 cursor-default" : "bg-[#F97316] hover:bg-[#EA580C] text-white"}`}
              >
                {isCur ? "Current plan" : p.intro_eligible ? `Start for ${formatINR(p.intro_price_paise)}` : "Upgrade"}
              </button>
            </div>
          );
        })}
      </div>

      {/* Top-up section */}
      <div className="card-base p-6">
        <div className="flex items-center gap-2 mb-1">
          <Zap className="h-4 w-4 text-orange-500" />
          <h2 className="font-semibold text-sm">Need a few more slots?</h2>
        </div>
        <p className="text-xs text-gray-500 mb-4">Top up your account without changing your plan. Slots never expire.</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {topups.map(t => (
            <div key={t.id} className="border border-gray-200 rounded-xl p-4 text-center hover:border-indigo-300 transition-colors">
              <div className="text-lg font-bold font-display text-[#F97316]">{t.slots}</div>
              <div className="text-xs text-gray-500 mb-2">slots</div>
              <div className="text-sm font-semibold">{formatINR(t.price)}</div>
              <button
                onClick={() => buyTopup(t)}
                disabled={buyingTopup === t.id}
                className="mt-2 w-full py-1.5 text-xs font-medium rounded-lg bg-orange-500 hover:bg-orange-600 text-white btn-press disabled:opacity-50"
              >
                {buyingTopup === t.id ? "Adding…" : "Buy"}
              </button>
            </div>
          ))}
        </div>
      </div>

      <p className="text-xs text-gray-500 text-center">
        {isSoloAgent
          ? "Earn up to ₹50 per agreement — or up to ₹1,500 when deposit is protected with Orello."
          : "Plans give your whole team more agreements and agents. Top-ups add slots instantly."}
      </p>
    </div>
  );
}